export function timeAgo(iso: string | Date | null): string {
  if (!iso) return '—';
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return 'in the future';
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

const APP_TIME_ZONE = 'Europe/Madrid';

export function formatTime(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleTimeString('es-ES', {
    timeZone: APP_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function formatDateTime(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleString('es-ES', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function rangeIso(range: '1h' | '6h' | '24h' | '7d' | '30d' | 'mtd'): {
  from: string;
  to: string;
} {
  const now = new Date();
  if (range === 'mtd') {
    // First day of the current month at 00:00 local time.
    const from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    return { from: from.toISOString(), to: now.toISOString() };
  }
  const ms = {
    '1h': 60 * 60_000,
    '6h': 6 * 60 * 60_000,
    '24h': 24 * 60 * 60_000,
    '7d': 7 * 24 * 60 * 60_000,
    '30d': 30 * 24 * 60 * 60_000,
  }[range];
  const raw = new Date(now.getTime() - ms);

  // Floor `from` to the bucket boundary so the first bucket always shows a
  // complete period instead of shrinking as time advances.
  const bucketMs: Record<string, number> = {
    '1h': 5 * 60_000,
    '6h': 30 * 60_000,
    '24h': 60 * 60_000,
    '7d': 60 * 60_000,
  };
  let from: Date;
  if (range === '30d') {
    from = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate(), 0, 0, 0, 0);
  } else {
    const step = bucketMs[range] ?? 60_000;
    from = new Date(Math.floor(raw.getTime() / step) * step);
  }
  return { from: from.toISOString(), to: now.toISOString() };
}

// Compact duration used by the uptime readouts ("45 min", "3 h 20 min", "2 d 4 h").
// Seconds only show up under a minute — downtime is never interesting to the second.
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const minRest = m % 60;
  if (h < 24) return minRest > 0 ? `${h} h ${minRest} min` : `${h} h`;
  const d = Math.floor(h / 24);
  const hRest = h % 24;
  return hRest > 0 ? `${d} d ${hRest} h` : `${d} d`;
}
